// src/pages/admin/AdminScanner.js
// Controls for the AI event scanner — kill switches, logs, review scanner events

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../lib/firebase';
import {
  collection, query, where, getDocs, doc,
  updateDoc, orderBy, limit, getDoc, setDoc
} from 'firebase/firestore';

import toast from 'react-hot-toast';
import { PageBack } from '../../components/shared/PageBack';
import { useAuth } from '../../lib/AuthContext';

const AdminScanner = () => {
  const navigate = useNavigate();
  const { isTownAdmin, townAdminScope } = useAuth();
  const [towns, setTowns] = useState([]);
  const [scannerEvents, setScannerEvents] = useState([]);
  const [logs, setLogs] = useState([]);
  const [globallyDisabled, setGloballyDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      // Load towns
      const townsSnap = await getDocs(collection(db, 'towns'));
      setTowns(townsSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Load global config
      const config = await getDoc(doc(db, 'config', 'scanner'));
      if (config.exists()) setGloballyDisabled(config.data().globallyDisabled || false);

      // Load scanner-sourced events
      const eventsQ = query(
        collection(db, 'events'),
        where('source', '==', 'scanner'),
        orderBy('createdAt', 'desc'),
        limit(50)
      );
      const eventsSnap = await getDocs(eventsQ);
      const allScannerEvents = eventsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setScannerEvents(isTownAdmin ? allScannerEvents.filter(e => e.townId === townAdminScope) : allScannerEvents);

      // Load recent scan logs
      const logsQ = query(
        collection(db, 'scanner_logs'),
        orderBy('runAt', 'desc'),
        limit(10)
      );
      const logsSnap = await getDocs(logsQ);
      const allLogs = logsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setLogs(isTownAdmin ? allLogs.filter(l => l.townId === townAdminScope) : allLogs);

    } catch (err) {
      toast.error('Failed to load scanner data');
    }
    setLoading(false);
  };

  // ── Global kill switch ────────────────────────────────────
  const toggleGlobal = async () => {
    const newVal = !globallyDisabled;
    await setDoc(doc(db, 'config', 'scanner'), { globallyDisabled: newVal }, { merge: true });
    setGloballyDisabled(newVal);
    toast.success(newVal ? '🛑 Scanner globally paused' : '✅ Scanner re-enabled globally');
  };

  // ── Per-town kill switch ──────────────────────────────────
  const toggleTownScanner = async (townId, current) => {
    const newVal = current === false ? true : false; // toggle
    await updateDoc(doc(db, 'towns', townId), { scannerEnabled: newVal });
    setTowns(t => t.map(town =>
      town.id === townId ? { ...town, scannerEnabled: newVal } : town
    ));
    toast.success(newVal ? 'Scanner on for this town' : 'Scanner paused for this town');
  };

  // ── Unpublish scanner event ───────────────────────────────
  const unpublishEvent = async (eventId) => {
    await updateDoc(doc(db, 'events', eventId), { status: 'hidden' });
    setScannerEvents(e => e.filter(ev => ev.id !== eventId));
    toast.success('Event removed from feed');
  };

  // ── Manual scan trigger ───────────────────────────────────
  const runManualScan = async (townId, townName) => {
    setScanning(townId);
    try {
      const result = await (await import("../../lib/firebase")).callManualScan({ townId, townName, county: 'Galway' });
      const resultData = result?.data || { eventsAdded: 0, errors: [] };
      toast.success(`Scan complete: ${resultData.eventsAdded} new events found`);
      loadAll();
    } catch (err) {
      console.error('Manual scan failed:', err);
      toast.error(`Scan failed — ${err?.message || 'unknown error'}`);
    }
    setScanning(null);
  };

  const fmtDate = (ts) => {
    if (!ts) return '—';
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-IE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh', padding: '52px 20px 40px' }}>

      {/* Back */}
      <button onClick={() => navigate('/admin')} style={btnBack}>
        <div style={backCircle}>←</div>
        <span style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>Admin</span>
      </button>

      <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 }}>
        🤖 Event Scanner
      </div>
      <div style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 500, marginBottom: 20 }}>
        Runs every Wednesday morning · Auto-publishes events found online
      </div>

      {/* ── GLOBAL KILL SWITCH — full admin only, this is platform-wide ── */}
      {!isTownAdmin && (
        <div style={sectionCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={sectionTitle}>Global kill switch</div>
              <div style={sectionSub}>Pause the scanner for all towns instantly</div>
            </div>
            <button
              onClick={toggleGlobal}
              style={{
                ...toggleBtn,
                background: globallyDisabled ? 'var(--error)' : 'var(--sage-deep)',
              }}
            >
              {globallyDisabled ? '🛑 PAUSED' : '✅ RUNNING'}
            </button>
          </div>
        </div>
      )}

      {/* ── PER-TOWN CONTROLS ── */}
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', margin: '20px 0 10px' }}>
        Per-town scanner
      </div>
      {towns.filter(t => t.status === 'live' && (!isTownAdmin || t.id === townAdminScope)).map(town => (
        <div key={town.id} style={sectionCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div>
              <div style={sectionTitle}>{town.name}</div>
              <div style={sectionSub}>{town.county}</div>
            </div>
            <button
              onClick={() => toggleTownScanner(town.id, town.scannerEnabled)}
              style={{
                ...toggleBtn,
                background: town.scannerEnabled === false ? '#8A8A8A' : 'var(--sage-deep)',
                fontSize: 11,
              }}
            >
              {town.scannerEnabled === false ? 'PAUSED' : 'ON'}
            </button>
          </div>
          <button
            onClick={() => runManualScan(town.id, town.name)}
            disabled={scanning === town.id}
            style={{
              width: '100%', background: 'var(--sage-pale)', color: 'var(--sage-dark)',
              border: 'none', borderRadius: 10, padding: '9px 14px',
              fontSize: 13, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer',
              opacity: scanning === town.id ? 0.6 : 1,
            }}
          >
            {scanning === town.id ? '⏳ Scanning...' : '▶ Run manual scan now'}
          </button>
        </div>
      ))}

      {/* ── SCANNER EVENTS ── */}
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', margin: '20px 0 10px' }}>
        Recently scanned events ({scannerEvents.length})
      </div>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}><div className="spinner" /></div>
      ) : scannerEvents.length === 0 ? (
        <div style={{ ...sectionCard, textAlign: 'center', color: 'var(--muted)', fontSize: 14 }}>
          No scanner events yet — run a scan to see results here
        </div>
      ) : scannerEvents.map(event => (
        <div key={event.id} style={sectionCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', marginBottom: 3 }}>
                {event.title}
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500, marginBottom: 4 }}>
                {event.venue} · {fmtDate(event.date)}
              </div>
              {event.sourceUrl && (
                <a
                  href={event.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 11, color: 'var(--sage-deep)', fontWeight: 600 }}
                >
                  📎 {event.sourceName || 'View source'}
                </a>
              )}
            </div>
            <button
              onClick={() => unpublishEvent(event.id)}
              style={{
                background: 'var(--error-pale)', color: 'var(--error)',
                border: 'none', borderRadius: 9, padding: '7px 12px',
                fontSize: 11, fontWeight: 700, fontFamily: 'Nunito',
                cursor: 'pointer', flexShrink: 0, marginLeft: 10,
              }}
            >
              Remove
            </button>
          </div>
        </div>
      ))}

      {/* ── SCAN LOGS ── */}
      {logs.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', margin: '20px 0 10px' }}>
            Recent scan runs
          </div>
          {logs.map(log => (
            <div key={log.id} style={{ ...sectionCard, padding: '12px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>{log.townName}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 500 }}>{fmtDate(log.runAt)}</div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500, marginTop: 2 }}>
                {log.sourcesScanned} source{log.sourcesScanned !== 1 ? 's' : ''} scanned · {log.eventsAdded} events added
                {log.pendingReview > 0 && (
                  <span> · {log.pendingReview} pending review</span>
                )}
                {log.errors?.length > 0 && (
                  <span style={{ color: 'var(--error)' }}> · {log.errors.length} errors</span>
                )}
              </div>
            </div>
          ))}
        </>
      )}
      <PageBack position="bottom" fallbackPath="/admin" />
    </div>
  );
};

const sectionCard = {
  background: 'var(--white)', borderRadius: 16,
  padding: '16px', marginBottom: 10,
  boxShadow: 'var(--shadow-sm)',
};
const sectionTitle = { fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 2 };
const sectionSub = { fontSize: 12, color: 'var(--muted)', fontWeight: 500 };
const toggleBtn = {
  border: 'none', borderRadius: 10, padding: '8px 14px',
  fontSize: 12, fontWeight: 800, color: 'white',
  fontFamily: 'Nunito', cursor: 'pointer',
};
const btnBack = {
  display: 'flex', alignItems: 'center', gap: 8,
  background: 'none', border: 'none', cursor: 'pointer', marginBottom: 20,
};
const backCircle = {
  width: 34, height: 34, background: 'var(--white)', borderRadius: '50%',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 15, color: 'var(--sage-dark)', boxShadow: 'var(--shadow-sm)',
};

export default AdminScanner;
